import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { getAdminProfile, updateAdminProfile } from '../../services/api';

/**
 * Fixes the backslash issue that comes from the backend:
 * "https://study.richmindx.in/uploads\\filename.jpg"  →  "https://study.richmindx.in/uploads/filename.jpg"
 * Already-clean URLs are returned unchanged.
 */
var fixImageUrl = function (url) {
  if (!url || typeof url !== 'string') return '';
  return url.replace(/\\/g, '/');
};

var ProfilePage = function () {
  var navigate    = useNavigate();
  var fileInputRef = useRef(null);

  var _pp = useState(null);  var profilePhotoPreview = _pp[0]; var setProfilePhotoPreview = _pp[1];
  var _pf = useState(null);  var profileFile         = _pf[0]; var setProfileFile         = _pf[1];
  var _il = useState(true);  var isLoading           = _il[0]; var setIsLoading           = _il[1];
  var _is = useState(false); var isSaving            = _is[0]; var setIsSaving            = _is[1];
  var _er = useState('');    var error               = _er[0]; var setError               = _er[1];
  var _su = useState('');    var successMsg          = _su[0]; var setSuccessMsg          = _su[1];

  /* Stored as returned by the API — already a full URL (with possible backslashes) */
  var _pi = useState('');    var profileImageUrl     = _pi[0]; var setProfileImageUrl     = _pi[1];

  var _ad = useState({
    firstName: '',
    lastName:  '',
    email:     '',
    mobile:    '',
    username:  '',
    password:  '',
  });
  var adminData    = _ad[0];
  var setAdminData = _ad[1];

  var _sp = useState(false); var showPass = _sp[0]; var setShowPass = _sp[1];

  var _fe = useState({}); var fieldErrors = _fe[0]; var setFieldErrors = _fe[1];

  /* Validation functions */
  var validateEmail = function (email) {
    if (!email.trim()) return 'Email is required.';
    var emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Invalid email format.';
    if (email.includes(' ')) return 'Email must not contain spaces.';
    if (email.includes('&')) return 'Email must not contain invalid characters like &.';
    var atCount = (email.match(/@/g) || []).length;
    if (atCount !== 1) return 'Email must contain exactly one @ symbol.';
    var domain = email.split('@')[1];
    if (!domain || !domain.includes('.')) return 'Email must include a valid domain.';
    return null;
  };

  var validatePhone = function (phone) {
    if (!phone.trim()) return 'Phone number is required.';
    if (!/^\d+$/.test(phone)) return 'Phone number must contain only numeric digits.';
    if (phone.length !== 10) return 'Phone number must be exactly 10 digits long.';
    return null;
  };

  var validateFirstName = function (name) {
    if (!name.trim()) return 'First name is required.';
    if (!/^[a-zA-Z\s\-']+$/.test(name)) return 'First name must contain only alphabetic characters, spaces, hyphens, and apostrophes.';
    return null;
  };

  var validateLastName = function (name) {
    if (name.trim() && !/^[a-zA-Z\s\-']+$/.test(name)) return 'Last name must contain only alphabetic characters, spaces, hyphens, and apostrophes.';
    return null;
  };

  var validatePassword = function (password, username, email) {
    if (!password.trim()) return null; // Optional
    if (password.length < 8 || password.length > 20) return 'Password must be between 8 and 20 characters.';
    if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter.';
    if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter.';
    if (!/\d/.test(password)) return 'Password must contain at least one numeric digit.';
    if (!/[@#$%&]/.test(password)) return 'Password must contain at least one special character (@, #, $, %, &).';
    if (password.includes(' ')) return 'Password must not contain spaces.';
    if (password === username) return 'Password must not be the same as username.';
    if (password === email) return 'Password must not be the same as email.';
    return null;
  };

  var validateUsername = function (username) {
    if (!username.trim()) return 'Username is required.';
    if (username.length < 4 || username.length > 20) return 'Username must be between 4 and 20 characters.';
    if (!/^[a-zA-Z]/.test(username)) return 'Username must start with a letter.';
    if (!/^[a-zA-Z0-9_.]+$/.test(username)) return 'Username can only contain letters, numbers, underscores, and dots.';
    if (username.includes(' ')) return 'Username must not contain spaces.';
    // Uniqueness check would require backend validation
    return null;
  };
  useEffect(function () {
    var isMounted = true;
    setIsLoading(true);
    setError('');

    getAdminProfile()
      .then(function (res) {
        var data = (res && res.data && res.data.data) ? res.data.data : null;
        if (!data || !isMounted) return;

        /* Split fullName → firstName + lastName */
        var fullName = (data.fullName || '').trim();
        var parts    = fullName ? fullName.split(' ') : [];
        var firstName = parts.shift() || '';
        var lastName  = parts.join(' ');

        setAdminData(function (prev) {
          return {
            firstName: firstName || prev.firstName,
            lastName:  lastName,
            email:     data.email    || prev.email,
            mobile:    data.mobile   || prev.mobile,
            username:  data.username || prev.username,
            password:  '',            /* never pre-fill password */
          };
        });

        /*
         * profileImage from the GET endpoint comes back as a full URL
         * but may contain backslashes — fix them.
         */
        setProfileImageUrl(fixImageUrl(data.profileImage || ''));
      })
      .catch(function () {
        if (isMounted) setError('Failed to load profile. Please try again.');
      })
      .finally(function () {
        if (isMounted) setIsLoading(false);
      });

    return function () { isMounted = false; };
  }, []);

  /* Revoke blob URL when preview changes */
  useEffect(function () {
    return function () {
      if (profilePhotoPreview && profilePhotoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(profilePhotoPreview);
      }
    };
  }, [profilePhotoPreview]);

  /* ── Handlers ── */
  var handlePhotoChange = function (e) {
    var file = e.target.files[0];
    if (!file) return;

    var fileName = (file.name || '').toLowerCase();
    var allowedTypes = ['image/jpeg', 'image/png'];
    var allowedExtensions = ['.jpg', '.jpeg', '.png'];
    var hasAllowedExtension = allowedExtensions.some(function (extension) {
      return fileName.endsWith(extension);
    });

    if (!allowedTypes.includes(file.type) || !hasAllowedExtension) {
      setError('Please select a JPG, JPEG, or PNG image.');
      e.target.value = '';
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setError('Profile image must be 2 MB or smaller.');
      e.target.value = '';
      return;
    }

    setError('');
    setProfileFile(file);
    setProfilePhotoPreview(URL.createObjectURL(file));
  };

  var handleSave = function () {
    if (isSaving || isLoading) return;

    var firstName = (adminData.firstName || '').trim();
    var lastName  = (adminData.lastName  || '').trim();

    if (!firstName) {
      setError('First name is required.');
      return;
    }

    var errors = {};
    errors.firstName = validateFirstName(adminData.firstName);
    errors.lastName = validateLastName(adminData.lastName);
    errors.email = validateEmail(adminData.email);
    errors.mobile = validatePhone(adminData.mobile);
    errors.username = validateUsername(adminData.username);
    errors.password = validatePassword(adminData.password, adminData.username, adminData.email);
    setFieldErrors(errors);

    if (Object.values(errors).some(function (e) { return e; })) {
      setError('Please fix the errors below.');
      return;
    }

    setIsSaving(true);
    setError('');
    setSuccessMsg('');

    var fullName = (firstName + (lastName ? ' ' + lastName : '')).trim();
    var email = (adminData.email || '').trim();
    var mobile = (adminData.mobile || '').trim();

    var formData = new FormData();
    formData.append('fullName', fullName);
    formData.append('username', adminData.username || '');
    formData.append('email', email);
    formData.append('mobile', mobile);
    if (adminData.password) formData.append('password', adminData.password);
    if (profileFile)        formData.append('profileImage', profileFile);

    updateAdminProfile(formData)
      .then(function (res) {
        var data = (res && res.data && res.data.data) ? res.data.data : null;

        if (data) {
          /* Update local state with fresh data from server */
          var updatedFullName = (data.fullName || '').trim();
          var parts2 = updatedFullName ? updatedFullName.split(' ') : [];
          var fn2 = parts2.shift() || '';
          var ln2 = parts2.join(' ');

          setAdminData(function (prev) {
            return {
              firstName: fn2 || prev.firstName,
              lastName:  ln2,
              email:     data.email    || prev.email,
              mobile:    data.mobile   || prev.mobile,
              username:  data.username || prev.username,
              password:  '',
            };
          });

          var freshImgUrl = fixImageUrl(data.profileImage || '');
          setProfileImageUrl(freshImgUrl);

          /*
          * Notify AdminLayout so the header avatar updates immediately
          * without a full page reload.
          */
          window.dispatchEvent(new CustomEvent('adminProfileUpdated', {
            detail: {
              fullName:     data.fullName || fullName,
              profileImage: freshImgUrl,
            }
          }));
        }

        /* Clear the staged file/preview after successful save */
        setProfileFile(null);
        setProfilePhotoPreview(null);
        setFieldErrors({});
        setSuccessMsg('Profile updated successfully!');

        /* Navigate back after short delay so user sees the success message */
        setTimeout(function () { navigate(-1); }, 1200);
      })
      .catch(function (err) {
        var msg = (err && err.response && err.response.data && err.response.data.message)
          ? err.response.data.message
          : 'Failed to update profile. Please try again.';
        setError(msg);
      })
      .finally(function () {
        setIsSaving(false);
      });
  };

  /* ── Derived display values ── */
  var initials = ((adminData.firstName[0] || '') + (adminData.lastName[0] || '')).toUpperCase() || 'AD';

  /*
   * Show priority:
   *   1. Local blob preview (just selected, not yet saved)
   *   2. URL returned by the API (already fixed)
   */
  var displayImageUrl = profilePhotoPreview || profileImageUrl;

  /* ── Styles ── */
  var css = `@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');

  .profile-page {
    font-family: 'DM Sans', sans-serif;
    color: #FFFFFF;
    padding: 18px 20px 34px;
    background: #120A3A;
    border-radius: 28px;
    position: relative;
    overflow: hidden;
    opacity: 0;
    transform: translateY(12px);
    animation: profilePageIn 0.44s ease forwards;
  }

  .profile-page::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background-image:
      radial-gradient(circle at 16% 14%, rgba(109,40,217,0.12) 0%, transparent 25%),
      radial-gradient(circle at 84% 12%, rgba(168,85,247,0.08) 0%, transparent 20%);
  }

  .profile-page-header {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 20px;
  }
  .back-btn {
    width: 42px; height: 42px;
    border-radius: 50%;
    background: rgba(109,40,217,0.15);
    border: 1.5px solid #312E81;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer;
    transition: background 0.18s ease, transform 0.18s ease;
    color: #A855F7;
    flex-shrink: 0;
  }
  .back-btn:hover { background: rgba(109,40,217,0.28); transform: translateY(-1px); }
  .profile-page-title {
    font-size: 2rem; font-weight: 700; color: #FFFFFF; margin: 0;
    letter-spacing: -0.4px;
  }

  .profile-card {
    position: relative;
    background: #20105E;
    border: 1px solid #312E81;
    border-radius: 28px;
    box-shadow: 0 20px 50px rgba(0,0,0,0.25);
    overflow: hidden;
    animation: cardIn 0.45s ease 0.1s both;
  }

  .status-message { margin: 14px 28px 0; font-size: 13px; font-weight: 600; }
  .status-message.error   { color: #F87171; }
  .status-message.success { color: #34D399; }

  .section-header {
    display: flex; align-items: center; gap: 12px;
    padding: 22px 28px 18px;
    border-bottom: 1px solid #312E81;
    background: rgba(109,40,217,0.08);
  }
  .section-icon {
    width: 34px; height: 34px; border-radius: 12px;
    background: rgba(168,85,247,0.15);
    display: flex; align-items: center; justify-content: center;
    color: #A855F7;
  }
  .section-title { font-size: 14px; font-weight: 700; color: #FFFFFF; }

  .photo-area {
    display: flex; align-items: center; gap: 24px;
    padding: 24px 28px;
    border-bottom: 1px solid #312E81;
  }
  .photo-circle-large {
    width: 104px; height: 104px;
    border-radius: 50%;
    background: linear-gradient(135deg, #3B82F6, #06B6D4);
    display: flex; align-items: center; justify-content: center;
    font-size: 2.2rem; font-weight: 800; color: #ffffff;
    overflow: hidden;
    border: 3px solid rgba(168,85,247,0.4);
    box-shadow: 0 8px 28px rgba(109,40,217,0.35);
    flex-shrink: 0;
  }
  .photo-circle-large img { width: 100%; height: 100%; object-fit: cover; }
  .photo-info { flex: 1; }
  .photo-info-title { font-size: 14px; font-weight: 700; color: #FFFFFF; margin-bottom: 4px; }
  .photo-info-sub   { font-size: 12px; color: #C4B5FD; margin-bottom: 14px; line-height: 1.5; }
  .change-img-btn {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 10px 20px;
    background: linear-gradient(135deg, #3B82F6, #06B6D4);
    color: #fff; border: none; border-radius: 12px;
    font-size: 13px; font-weight: 700; cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    box-shadow: 0 4px 16px rgba(59,130,246,0.3);
    transition: opacity 0.18s ease, transform 0.18s ease;
  }
  .change-img-btn:hover { opacity: 0.92; transform: translateY(-1px); }

  .form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px 26px;
    padding: 28px 28px 24px;
  }
  .form-field { display: flex; flex-direction: column; gap: 8px; }
  .form-label {
    font-size: 11px; font-weight: 700; color: #A855F7;
    letter-spacing: 0.06em; text-transform: uppercase;
  }
  .required-star { color: #EC4899; margin-left: 2px; }
  .form-input {
    padding: 12px 15px;
    border: 1.5px solid #312E81;
    border-radius: 14px;
    font-size: 14px; font-weight: 500; color: #FFFFFF;
    background: #1A0B4D; outline: none;
    transition: border-color 0.18s ease, box-shadow 0.18s ease;
    box-sizing: border-box;
    width: 100%;
    font-family: 'DM Sans', sans-serif;
  }
  .form-input:focus {
    border-color: #6D28D9;
    box-shadow: 0 0 0 3px rgba(109,40,217,0.25);
    background: #20105E;
  }
  .form-input::placeholder { color: #6D28D9; }
  .form-input:disabled { background: rgba(26,11,77,0.5); color: #6D28D9; cursor: not-allowed; }
  .field-error { color: #F87171; font-size: 12px; font-weight: 600; margin-top: 4px; }

  .input-wrapper { position: relative; display: flex; align-items: center; }
  .input-wrapper .form-input { padding-right: 44px; }
  .eye-btn {
    position: absolute; right: 12px;
    background: none; border: none; cursor: pointer;
    color: #6D28D9; display: flex; align-items: center;
    padding: 0; transition: color 0.18s ease;
  }
  .eye-btn:hover { color: #A855F7; }

  .profile-footer {
    display: flex; justify-content: flex-end; gap: 14px;
    padding: 18px 28px 24px;
    border-top: 1px solid #312E81;
    background: rgba(109,40,217,0.06);
  }
  .btn-cancel {
    padding: 12px 24px;
    background: transparent; color: #C4B5FD;
    border: 1.5px solid #312E81;
    border-radius: 14px; font-size: 14px; font-weight: 700;
    cursor: pointer; font-family: 'DM Sans', sans-serif;
    transition: background 0.18s ease;
  }
  .btn-cancel:hover { background: rgba(109,40,217,0.2); }
  .btn-save {
    padding: 12px 28px;
    background: linear-gradient(135deg, #6D28D9, #A855F7);
    color: #ffffff; border: none; border-radius: 14px;
    font-size: 14px; font-weight: 700; cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    transition: opacity 0.18s ease, transform 0.18s ease;
    box-shadow: 0 8px 24px rgba(109,40,217,0.35);
  }
  .btn-save:hover { opacity: 0.94; transform: translateY(-1px); }
  .btn-save:disabled { opacity: 0.65; cursor: not-allowed; transform: none; }

  .loading-overlay {
    display: flex; align-items: center; justify-content: center;
    padding: 60px 28px;
    color: #C4B5FD; font-size: 14px; font-weight: 600;
    gap: 10px;
  }

  @keyframes profilePageIn {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes cardIn {
    from { opacity: 0; transform: translateY(18px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  @media (max-width: 700px) {
    .profile-page { padding: 16px 14px 26px; }
    .profile-page-header { flex-wrap: wrap; gap: 12px; }
    .photo-area { flex-direction: column; align-items: stretch; }
    .form-grid { grid-template-columns: 1fr; }
    .profile-footer { flex-direction: column; align-items: stretch; }
    .btn-cancel, .btn-save { width: 100%; }
  }
`;

  return (
    React.createElement(AdminLayout, null,
      React.createElement('style', null, css),
      React.createElement('div', { className: 'profile-page' },

        /* ── Page Header ── */
        React.createElement('div', { className: 'profile-page-header' },
          React.createElement('button', {
            className: 'back-btn',
            onClick: function () { navigate(-1); },
            title: 'Go back'
          },
            React.createElement('svg', {
              width: '18', height: '18', viewBox: '0 0 24 24',
              fill: 'none', stroke: 'currentColor',
              strokeWidth: '2.5', strokeLinecap: 'round', strokeLinejoin: 'round'
            },
              React.createElement('polyline', { points: '15 18 9 12 15 6' })
            )
          ),
          React.createElement('h1', { className: 'profile-page-title' }, 'Profile')
        ),

        /* ── Card ── */
        React.createElement('div', { className: 'profile-card' },

          /* Section Header */
          React.createElement('div', { className: 'section-header' },
            React.createElement('div', { className: 'section-icon' },
              React.createElement('svg', {
                width: '17', height: '17', viewBox: '0 0 24 24',
                fill: 'none', stroke: 'currentColor',
                strokeWidth: '2', strokeLinecap: 'round', strokeLinejoin: 'round'
              },
                React.createElement('path', { d: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2' }),
                React.createElement('circle', { cx: '12', cy: '7', r: '4' })
              )
            ),
            React.createElement('span', { className: 'section-title' }, 'Basic Information')
          ),

          /* Status messages */
          error      ? React.createElement('div', { className: 'status-message error'   }, error)      : null,
          successMsg ? React.createElement('div', { className: 'status-message success' }, successMsg) : null,

          /* ── Loading skeleton ── */
          isLoading
            ? React.createElement('div', { className: 'loading-overlay' },
                React.createElement('svg', {
                  width: '20', height: '20', viewBox: '0 0 24 24',
                  fill: 'none', stroke: '#db2777', strokeWidth: '2.5',
                  strokeLinecap: 'round', strokeLinejoin: 'round',
                  style: { animation: 'spin 1s linear infinite' }
                },
                  React.createElement('path', { d: 'M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83' })
                ),
                'Loading profile...',
                React.createElement('style', null, '@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }')
              )
            : React.createElement(React.Fragment, null,

                /* ── Photo Upload ── */
                React.createElement('div', { className: 'photo-area' },
                  React.createElement('div', { className: 'photo-circle-large' },
                    displayImageUrl
                      ? React.createElement('img', {
                          src: displayImageUrl,
                          alt: 'Profile',
                          onError: function (e) { e.target.style.display = 'none'; }
                        })
                      : initials
                  ),
                  React.createElement('div', { className: 'photo-info' },
                    React.createElement('div', { className: 'photo-info-title' }, 'Profile Photo'),
                    React.createElement('div', { className: 'photo-info-sub'   }, 'Upload an image below 2 MB. Accepted formats: JPG, PNG'),
                    React.createElement('button', {
                      className: 'change-img-btn',
                      onClick: function () { if (fileInputRef.current) fileInputRef.current.click(); }
                    },
                      React.createElement('svg', {
                        width: '15', height: '15', viewBox: '0 0 24 24',
                        fill: 'none', stroke: 'currentColor',
                        strokeWidth: '2', strokeLinecap: 'round', strokeLinejoin: 'round'
                      },
                        React.createElement('path', { d: 'M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z' }),
                        React.createElement('circle', { cx: '12', cy: '13', r: '4' })
                      ),
                      profileFile ? 'Change Again' : 'Change Image'
                    ),
                    React.createElement('input', {
                      ref: fileInputRef,
                      type: 'file',
                      accept: '.jpg,.jpeg,.png,image/jpeg,image/png',
                      style: { display: 'none' },
                      onChange: handlePhotoChange
                    })
                  )
                ),

                /* ── Form Fields ── */
                React.createElement('div', { className: 'form-grid' },

                  /* First Name */
                  React.createElement('div', { className: 'form-field' },
                    React.createElement('label', { className: 'form-label' },
                      'First Name ', React.createElement('span', { className: 'required-star' }, '*')
                    ),
                    React.createElement('input', {
                      className: 'form-input',
                      type: 'text',
                      placeholder: 'Enter first name',
                      value: adminData.firstName,
                      onChange: function (e) {
                        var v = e.target.value;
                        setAdminData(function (prev) { return Object.assign({}, prev, { firstName: v }); });
                        setFieldErrors(function (prev) { return Object.assign({}, prev, { firstName: null }); });
                      }
                    }),
                    fieldErrors.firstName ? React.createElement('div', { className: 'field-error' }, fieldErrors.firstName) : null
                  ),

                  /* Last Name */
                  React.createElement('div', { className: 'form-field' },
                    React.createElement('label', { className: 'form-label' }, 'Last Name'),
                    React.createElement('input', {
                      className: 'form-input',
                      type: 'text',
                      placeholder: 'Enter last name',
                      value: adminData.lastName,
                      onChange: function (e) {
                        var v = e.target.value;
                        setAdminData(function (prev) { return Object.assign({}, prev, { lastName: v }); });
                        setFieldErrors(function (prev) { return Object.assign({}, prev, { lastName: null }); });
                      }
                    }),
                    fieldErrors.lastName ? React.createElement('div', { className: 'field-error' }, fieldErrors.lastName) : null
                  ),

                  /* Email */
                  React.createElement('div', { className: 'form-field' },
                    React.createElement('label', { className: 'form-label' }, 'Email ', React.createElement('span', { className: 'required-star' }, '*')),
                    React.createElement('input', {
                      className: 'form-input',
                      type: 'email',
                      placeholder: 'Enter email address',
                      value: adminData.email,
                      onChange: function (e) {
                        var v = e.target.value;
                        setAdminData(function (prev) { return Object.assign({}, prev, { email: v }); });
                        setFieldErrors(function (prev) { return Object.assign({}, prev, { email: null }); });
                      }
                    }),
                    fieldErrors.email ? React.createElement('div', { className: 'field-error' }, fieldErrors.email) : null
                  ),

                  /* Phone */
                  React.createElement('div', { className: 'form-field' },
                    React.createElement('label', { className: 'form-label' }, 'Phone Number ', React.createElement('span', { className: 'required-star' }, '*')),
                    React.createElement('input', {
                      className: 'form-input',
                      type: 'tel',
                      placeholder: 'Enter phone number',
                      value: adminData.mobile,
                      onChange: function (e) {
                        var v = e.target.value;
                        setAdminData(function (prev) { return Object.assign({}, prev, { mobile: v }); });
                        setFieldErrors(function (prev) { return Object.assign({}, prev, { mobile: null }); });
                      }
                    }),
                    fieldErrors.mobile ? React.createElement('div', { className: 'field-error' }, fieldErrors.mobile) : null
                  ),

                  /* Username */
                  React.createElement('div', { className: 'form-field' },
                    React.createElement('label', { className: 'form-label' },
                      'User Name ', React.createElement('span', { className: 'required-star' }, '*')
                    ),
                    React.createElement('input', {
                      className: 'form-input',
                      type: 'text',
                      placeholder: 'Enter username',
                      value: adminData.username,
                      onChange: function (e) {
                        var v = e.target.value;
                        setAdminData(function (prev) { return Object.assign({}, prev, { username: v }); });
                        setFieldErrors(function (prev) { return Object.assign({}, prev, { username: null }); });
                      }
                    }),
                    fieldErrors.username ? React.createElement('div', { className: 'field-error' }, fieldErrors.username) : null
                  ),

                  /* Password */
                  React.createElement('div', { className: 'form-field' },
                    React.createElement('label', { className: 'form-label' }, 'New Password'),
                    React.createElement('div', { className: 'input-wrapper' },
                      React.createElement('input', {
                        className: 'form-input',
                        type: showPass ? 'text' : 'password',
                        placeholder: 'Leave blank to keep current',
                        value: adminData.password,
                        onChange: function (e) {
                          var v = e.target.value;
                          setAdminData(function (prev) { return Object.assign({}, prev, { password: v }); });
                          setFieldErrors(function (prev) { return Object.assign({}, prev, { password: null }); });
                        }
                      }),
                      React.createElement('button', {
                        type: 'button',
                        className: 'eye-btn',
                        onClick: function () { setShowPass(function (p) { return !p; }); },
                        tabIndex: -1
                      },
                        showPass
                          ? React.createElement('svg', {
                              width: '17', height: '17', viewBox: '0 0 24 24',
                              fill: 'none', stroke: 'currentColor',
                              strokeWidth: '2', strokeLinecap: 'round', strokeLinejoin: 'round'
                            },
                              React.createElement('path', { d: 'M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94' }),
                              React.createElement('path', { d: 'M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19' }),
                              React.createElement('line', { x1: '1', y1: '1', x2: '23', y2: '23' })
                            )
                          : React.createElement('svg', {
                              width: '17', height: '17', viewBox: '0 0 24 24',
                              fill: 'none', stroke: 'currentColor',
                              strokeWidth: '2', strokeLinecap: 'round', strokeLinejoin: 'round'
                            },
                              React.createElement('path', { d: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z' }),
                              React.createElement('circle', { cx: '12', cy: '12', r: '3' })
                            )
                      )
                    ),
                    fieldErrors.password ? React.createElement('div', { className: 'field-error' }, fieldErrors.password) : null
                  )
                ),

                /* ── Footer Actions ── */
                React.createElement('div', { className: 'profile-footer' },
                  React.createElement('button', {
                    className: 'btn-cancel',
                    onClick: function () { navigate(-1); },
                    disabled: isSaving
                  }, 'Cancel'),
                  React.createElement('button', {
                    className: 'btn-save',
                    onClick: handleSave,
                    disabled: isSaving || isLoading
                  }, isSaving ? 'Saving...' : 'Save Changes')
                )
              ) /* end !isLoading fragment */
        ) /* end profile-card */
      ) /* end profile-page */
    )
  );
};

export default ProfilePage;

